import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Shield, Clock, Eye } from "lucide-react";
import { fetchArticleThunk } from "../../redux/news/newsActionThunks";
import { tierColors } from "../../utils/credibilityTiers";
import { AIDetectionTrustBadge } from "../../components/Compliance/ComplianceNotices";

const AuthorByline = ({ article }) => {
  const { t } = useTranslation();
  const colors = tierColors(article.author_tier);
  const initial = (article.author_username || "?")[0].toUpperCase();

  return (
    <div className="py-5 px-5 mb-8 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
      <div className="flex items-center gap-4">
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg text-white flex-shrink-0 bg-gradient-to-br ${colors.gradient}`}
        >
          {initial}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold">{article.author_username}</span>
            {article.author_tier && (
              <span className={`flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} border ${colors.border}`}>
                <Shield size={10} /> {article.author_tier}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
            {article.published_at && (
              <span className="flex items-center gap-1">
                <Clock size={12} /> {new Date(article.published_at).toLocaleString()}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Eye size={12} /> {t('news.viewsCount', { count: article.view_count })}
            </span>
          </div>
        </div>
      </div>
      {/* EU AI Act Art 50 transparency -- Elite-original articles are the
          ones that actually go through the ai_detection_score/flag pipeline
          stage plus mandatory human moderator review
          (25b_PUBLISHED_NEWS_DESIGN.md). `active` reflects the real,
          server-computed ai_detection_active flag (true only once a real
          detection vendor is wired in, not the current NoOpAIDetectionProvider
          stub) so this never claims AI screening happened when it didn't --
          the public schema still never exposes the raw per-article score/flag. */}
      <div className="mt-3 pt-3 border-t border-white/[0.06]">
        <AIDetectionTrustBadge active={!!article.ai_detection_active} />
      </div>
    </div>
  );
};

const NewsArticleDetailPage = () => {
  const { t } = useTranslation();
  const { articleId } = useParams();
  const dispatch = useDispatch();
  const article = useSelector((s) => s.news?.articleById?.[Number(articleId)]);
  const { articleLoading, articleError } = useSelector((s) => s.news || {});

  useEffect(() => {
    dispatch(fetchArticleThunk(articleId));
  }, [dispatch, articleId]);

  if (articleLoading && !article) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: "#09090f" }}>
        <div className="w-10 h-10 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (articleError || !article) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-100" style={{ background: "#09090f" }}>
        <div className="text-center">
          <p className="text-gray-400 mb-4">{t('news.articleNotFound')}</p>
          <Link to="/news" className="text-violet-400 hover:text-violet-300">
            {t('news.backToPublishedNews')}
          </Link>
        </div>
      </div>
    );
  }

  const isElite = article.source_type === "elite_original";

  return (
    <div className="min-h-full" style={{ background: "#09090f" }}>
      <div
        className="pointer-events-none"
        style={{
          position: "fixed",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          height: 420,
          background: "radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.14) 0%, transparent 65%)",
        }}
      />

      <div className="relative max-w-2xl mx-auto p-6 pb-24">
        <Link to="/news" className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition text-sm">
          <ArrowLeft size={16} /> {t('news.backToPublishedNews')}
        </Link>

        <div className="flex items-center gap-2 mb-5">
          {article.tickers?.map((t) => (
            <span key={t} className="bg-emerald-500/10 text-emerald-300 text-xs px-2.5 py-1 rounded-lg font-medium">
              {t}
            </span>
          ))}
        </div>

        <h1
          className="font-bold text-white mb-6 leading-[1.15] tracking-tight"
          style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}
        >
          {article.title}
        </h1>

        {isElite && <AuthorByline article={article} />}

        <div
          className="text-gray-300 whitespace-pre-wrap"
          style={{ fontSize: "1.0625rem", lineHeight: 1.8 }}
        >
          {article.body_content}
        </div>
      </div>
    </div>
  );
};

export default NewsArticleDetailPage;
